import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { QRCodeCanvas } from "qrcode.react";
import {
  Mail,
  Globe,
  Phone,
  MapPin,
  Linkedin,
  Facebook,
  Instagram,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { usersData } from "../../../data/usersData";
import NotFound from "../NotFound";

const networks = [
  {
    id: "scan-global-solutions",
    name: "Scan Global Solutions",
    img: "/images/OurNetwork/2.png",
    tags: ["Canada", "UAE"],
    socials: [
      {
        icon: Instagram,
        link: "https://www.instagram.com/scanglobalsolutions?igsh=MTV6MzA3NmMzcDFsbQ==",
      },
      {
        icon: Linkedin,
        link: "https://www.linkedin.com/company/scan-global-solutions-uae/",
      },
    ],
  },
  {
    id: "scan-global-realty",
    name: "Scan Global Realty",
    img: "/images/OurNetwork/3.png",
    tags: ["Canada", "UAE"],
    socials: [
      {
        icon: Facebook,
        link: "https://www.facebook.com/ScanGlobalRealty",
      },
      {
        icon: Instagram,
        link: "https://www.instagram.com/scanglobalrealty?igsh=djk0ZHYxbXIwaWdm",
      },
      {
        icon: Linkedin,
        link: "https://www.linkedin.com/company/scanglobalrealty/",
      },
    ],
  },
  {
    id: "scan-accounting",
    name: "Scan Accounting",
    img: "/images/OurNetwork/12.png",
    tags: ["Canada"],
    socials: [
      { icon: Globe, link: null },
      { icon: Linkedin, link: null },
    ],
  },
  {
    id: "scan-business-brokerage",
    name: "Scan Business Brokerage",
    img: "/images/OurNetwork/42.png",
    tags: ["Visit"],
    socials: [{ icon: Linkedin, link: null }],
  },
];

const UserProfile = () => {
  const { username } = useParams();
  const [isFlipped, setIsFlipped] = useState(false);

  // Helper to normalize strings (remove spaces, lowercase) for loose matching
  const normalize = (str) => str?.toLowerCase().replace(/\s+/g, "");

  // 1. Try Direct Lookup (e.g. "wahaj")
  let user = usersData[username?.toLowerCase()];

  // 2. If not found, try finding by Name or Slug loosely
  if (!user && username) {
    const searchParam = normalize(username);
    user = Object.values(usersData).find((u) => {
      const normalizedName = normalize(u.name);
      const normalizedSlug = normalize(u.slug);
      return (
        normalizedName === searchParam ||
        normalizedSlug === searchParam ||
        normalizedName.includes(searchParam)
      );
    });
  }

  if (!user) {
    return <NotFound />;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <>
      <Helmet>
        <title>{user.name} | ScanGlobal Holdings</title>
        <meta name="description" content={user.description} />
        <meta property="og:title" content={user.name} />
        <meta property="og:description" content={user.description} />
      </Helmet>

      <div className="min-h-screen bg-black text-white relative overflow-hidden selection:bg-gold selection:text-black">
        {/* Background Gradients */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-gold/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-6xl mx-auto px-4 py-14 relative z-10">
          {/* MAIN PROFILE CARD */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col gap-12"
          >
            {/* MAIN PROFILE CARD */}
            <motion.div
              variants={itemVariants}
              className="w-full relative group"
            >
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-2xl relative overflow-hidden">
                {/* Hover Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-center">
                  {/* Left: Image (Flip Container) */}
                  <div
                    className="flex justify-center lg:justify-start"
                    style={{ perspective: "1000px" }}
                    onMouseEnter={() => setIsFlipped(true)}
                    onMouseLeave={() => setIsFlipped(false)}
                  >
                    <motion.div
                      className="relative w-72 h-72 lg:w-80 lg:h-80"
                      initial={false}
                      animate={{ rotateY: isFlipped ? 180 : 0 }}
                      transition={{
                        duration: 0.6,
                        animationDirection: "normal",
                      }}
                      style={{ transformStyle: "preserve-3d" }}
                    >
                      {/* FRONT FACE (Image) */}
                      <div
                        className="absolute inset-0"
                        style={{ backfaceVisibility: "hidden" }}
                      >
                        <div className="absolute inset-0 bg-gold blur-lg opacity-20 rounded-2xl transform rotate-3" />
                        <img
                          src={user.image}
                          alt={user.name}
                          className="w-full h-full rounded-2xl object-cover shadow-2xl border border-white/10 relative z-10"
                        />
                        {/* Decorative corner accents */}
                        <div className="absolute -top-3 -left-3 w-12 h-12 border-t-2 border-l-2 border-gold/50 rounded-tl-xl z-20" />
                        <div className="absolute -bottom-3 -right-3 w-12 h-12 border-b-2 border-r-2 border-gold/50 rounded-br-xl z-20" />
                      </div>

                      {/* BACK FACE (QR Code) */}
                      <div
                        className="absolute inset-0 bg-black/90 rounded-2xl border border-gold/30 flex flex-col items-center justify-center text-center p-4"
                        style={{
                          backfaceVisibility: "hidden",
                          transform: "rotateY(180deg)",
                        }}
                      >
                        <p className="text-gold text-sm font-bold mb-3 uppercase tracking-wider">
                          Scan to Connect
                        </p>
                        <div className="p-2 bg-white rounded-xl shadow-[0_0_15px_rgba(197,174,122,0.3)]">
                          <QRCodeCanvas
                            value={user.value || window.location.href}
                            size={160}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="H"
                            includeMargin={false}
                            imageSettings={{
                              src: "/images/color-logo.png",
                              x: undefined,
                              y: undefined,
                              height: 24,
                              width: 24,
                              excavate: true,
                            }}
                          />
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Right: Info */}
                  <div className="lg:col-span-2 text-center lg:text-left space-y-6">
                    <div>
                      <h1 className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400 mb-2">
                        {user.name}
                      </h1>
                      <p className="text-gold text-base font-medium tracking-wide uppercase">
                        {user.position}
                      </p>
                      {user.title && (
                        <p className="text-gray-400 text-sm mt-1">
                          {user.title}
                        </p>
                      )}
                    </div>

                    <p className="text-gray-300 leading-relaxed text-base max-w-2xl mx-auto lg:mx-0">
                      {user.description}
                    </p>

                    {/* Contact Links */}
                    <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                      <a
                        href={`mailto:${user.email}`}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gold/10 text-gold border border-gold/20 hover:bg-gold hover:text-black transition-all duration-300 group/btn"
                      >
                        <Mail className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                        <span className="text-sm font-medium">
                          {user.email}
                        </span>
                      </a>

                      {user.website && (
                        <a
                          href={`https://${user.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-400 hover:text-black transition-all duration-300 group/btn"
                        >
                          <Globe className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                          <span className="text-sm font-medium">
                            {user.website}
                          </span>
                        </a>
                      )}

                      {/* {user.linkedin && (
                      <Link
                        to={user.linkedin}
                        target="_blank"
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#0077b5] text-white border border-[#0077b5] hover:bg-[#006097] hover:border-[#006097] transition-all duration-300 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 hover:-translate-y-0.5"
                      >
                        <Linkedin className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Connect on LinkedIn
                        </span>
                      </Link>
                    )} */}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* LOCATIONS GRID */}
            {user.locations && user.locations.length > 0 && (
              <motion.div variants={itemVariants}>
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <span className="w-8 h-1 bg-gold rounded-full" />
                  Global Presence
                </h3>

                {/* LinkedIn CTA */}
                {user.linkedin && (
                  <div className="my-8 pt-8 border-t border-white/10 flex justify-center lg:justify-start">
                    <Link
                      to={user.linkedin}
                      target="_blank"
                      className="bg-gold hover:bg-white text-black px-8 py-3 rounded-full font-bold flex items-center gap-2 transition-all duration-300
                   shadow-[0_0_20px_rgba(197,174,122,0.3)]
                   hover:shadow-[0_0_30px_rgba(255,255,255,0.4)]"
                    >
                      <Linkedin className="w-5 h-5" />
                      Connect on LinkedIn
                    </Link>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {user.locations.map((loc, i) => (
                    <div
                      key={i}
                      className="bg-white/5 backdrop-blur-xl border border-white/10 p-6 rounded-2xl hover:border-gold/30 transition-colors duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-gold/10 rounded-xl text-gold">
                          <MapPin className="w-6 h-6" />
                        </div>
                        <div>
                          <h4 className="text-xl font-bold text-white mb-2">
                            {loc.country}
                          </h4>
                          <p className="text-gray-400 text-sm leading-relaxed mb-3">
                            {loc.address}
                          </p>
                          {loc.phone && (
                            <div className="flex items-center gap-2 text-gold">
                              <Phone className="w-4 h-4" />
                              <span className="text-sm font-medium">
                                {loc.phone}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* BACKGROUND & EXPERIENCE */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ABOUT SECTION */}
              <motion.div
                variants={itemVariants}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 h-full"
              >
                <h2 className="text-2xl font-bold mb-6 text-gold">About Me</h2>

                {/* CASE 1: about is ARRAY (Maricel-style mapped content) */}
                {Array.isArray(user.about) && user.about.length > 0 && (
                  <div className="space-y-8">
                    {user.about.map((section, index) => (
                      <div key={index}>
                        {section.title && (
                          <h3 className="text-lg font-semibold text-white mb-3">
                            {section.title}
                          </h3>
                        )}

                        {/* Paragraph content */}
                        {section.content && (
                          <div className="space-y-2 text-gray-300 leading-relaxed text-sm lg:text-base">
                            {section.content.map((text, i) => (
                              <p key={i}>{text}</p>
                            ))}
                          </div>
                        )}

                        {/* Bullet list */}
                        {section.list && (
                          <ul className="list-disc list-inside space-y-2 text-gray-300 mt-3">
                            {section.list.map((item, i) => (
                              <li className="text-sm lg:text-base" key={i}>
                                {item}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* QR CODE & BRANDING */}
              <motion.div
                variants={itemVariants}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 flex flex-col items-center justify-center text-center h-full space-y-6"
              >
                <h3 className="text-2xl font-bold text-white mb-2">
                  Scan Global Holdings
                </h3>
                <div className="flex gap-2 justify-center mb-2">
                  <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-sm text-gray-300">
                    Canada
                  </span>
                  <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-sm text-gray-300">
                    UAE
                  </span>
                </div>

                {/* QR Code Container */}
                <div className="p-4 bg-white rounded-2xl shadow-lg border-4 border-gold/30">
                  <QRCodeCanvas
                    value={user.value || window.location.href}
                    size={180}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="H"
                    includeMargin={false}
                    imageSettings={{
                      src: "/images/color-logo.png",
                      x: undefined,
                      y: undefined,
                      height: 34,
                      width: 34,
                      excavate: true,
                    }}
                  />
                </div>

                <div className="flex gap-4 justify-center mt-2">
                  {user.facebook && (
                    <Link
                      to={user.facebook}
                      target="_blank"
                      className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300 text-white shadow-lg"
                    >
                      <Facebook className="w-4 h-4" />
                    </Link>
                  )}

                  {user.instagram && (
                    <Link
                      to={user.instagram}
                      target="_blank"
                      className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300 text-white shadow-lg"
                    >
                      <Instagram className="w-4 h-4" />
                    </Link>
                  )}
                  {user.linkedin && (
                    <Link
                      to={user.linkedin}
                      target="_blank"
                      className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300 text-white shadow-lg"
                    >
                      <Linkedin className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </motion.div>
            </div>

            {/* SGH NETWORK SECTION */}
            <motion.div variants={itemVariants} className="mt-10">
              <div className="flex flex-col items-center justify-center text-center mb-10">
                <div className="w-16 h-1 bg-gradient-to-r from-transparent via-gold to-transparent mb-4" />
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                  Network Firms of SGH
                </h2>
                <p className="text-gray-400">Our Global Ecosystem</p>
              </div>

              {/* OTHER NETWORKS GRID */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {networks.map((network) => (
                  <div
                    key={network.id}
                    className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 hover:-translate-y-2 transition-all duration-300 group"
                  >
                    {/* Logo */}
                    <div className="h-16 flex items-center justify-center mb-4">
                      <img
                        src={network.img}
                        alt={network.name}
                        className="h-12 w-auto opacity-80 group-hover:opacity-100 transition-opacity"
                      />
                    </div>

                    {/* Name */}
                    <h3 className="font-bold text-white mb-4 h-12 flex items-center justify-center">
                      {network.name}
                    </h3>

                    {/* Tags */}
                    {Array.isArray(network.tags) && (
                      <div className="flex flex-wrap gap-2 justify-center mb-6 h-8 items-center">
                        {network.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 bg-white/5 border border-white/10 rounded-full text-[10px] text-gray-400 font-semibold uppercase tracking-wider"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Social icons */}
                    {Array.isArray(network.socials) && (
                      <div className="flex gap-2 justify-center">
                        {network.socials.map(({ icon: Icon, link }) => (
                          <a
                            key={link}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center hover:bg-gold hover:text-black transition-colors duration-300 text-white"
                          >
                            <Icon className="w-4 h-4" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-12 text-center text-gray-400 text-sm font-medium">
                <p>
                  © {new Date().getFullYear()} Scanglobal — All rights reserved
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;
